import React from 'react';
import { Row, Col, Card, Badge } from 'react-bootstrap';
import { FaReceipt, FaCreditCard, FaUsers, FaChartBar } from 'react-icons/fa';
import ReportCard from './ReportCard';
import { PieChartJS, generateChartColors, prepareChartData } from './ChartComponents';

const VouchersReport = ({ dashboardData, formatCurrency }) => {
  // Prepare pie chart data for voucher types
  const voucherTypesChartData = prepareChartData(
    dashboardData.vouchers.voucherTypes.map(type => type.type),
    [{
      label: 'Voucher Amount',
      data: dashboardData.vouchers.voucherTypes.map(type => type.amount),
    }],
    generateChartColors(dashboardData.vouchers.voucherTypes.length)
  );

  return (
    <>
      <Row className="g-4 mb-4">
        <Col lg={3}>
          <ReportCard
            title="Total Ledgers"
            value={dashboardData.vouchers.totalLedgers.toLocaleString()}
            icon={<FaUsers />}
            color="info"
            subtitle="Total ledger accounts"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Total Vouchers"
            value={dashboardData.vouchers.totalVouchers.toLocaleString()}
            icon={<FaReceipt />}
            color="primary"
            subtitle="Vouchers created"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Total Amount"
            value={formatCurrency(dashboardData.vouchers.totalAmount)}
            icon={<FaChartBar />}
            color="success"
            subtitle="Total voucher value"
          />
        </Col>
        <Col lg={3}>
          <ReportCard
            title="Outstanding"
            value={formatCurrency(dashboardData.vouchers.outstandingPayments)}
            icon={<FaCreditCard />}
            color="danger"
            subtitle="Pending payments"
          />
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-5">
            <Card.Header className="bg-transparent">
              <h5 className="mb-0">
                <FaCreditCard className="me-2 text-white" />
                Outstanding Payments
              </h5>
              <small className="text-muted">Top 10 accounts with pending payments</small>
            </Card.Header>
            <Card.Body>
              {dashboardData.vouchers.outstandingPaymentsRecords.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Ledger Name</th>
                        <th>Closing Balance</th>
                        <th>Parent Group</th>
                        <th>State</th>
                        <th>Legacy Alter ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.vouchers.outstandingPaymentsRecords.map((payment, index) => (
                        <tr key={index}>
                          <td>
                            <Badge bg={index < 3 ? 'danger' : 'secondary'} className="fs-6">
                              #{index + 1}
                            </Badge>
                          </td>
                          <td className="fw-medium">{payment.partyName}</td>
                          <td className="fw-bold text-danger">{formatCurrency(payment.balance)}</td>
                          <td>
                            <Badge bg="info">{payment.parentGroup}</Badge>
                          </td>
                          <td>
                            <small className="text-muted">{payment.state}</small>
                          </td>
                          <td>
                            <small className="text-muted">{payment.legacyAlterId}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaCreditCard size={60} className="text-muted mb-3" />
                  <p className="text-muted">No outstanding payments found</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {dashboardData.vouchers.voucherTypes.length > 0 ? (
            <PieChartJS
              data={voucherTypesChartData}
              title="Voucher Types"
              height={350}
              options={{
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = formatCurrency(context.parsed);
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : '0';
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          ) : (
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <FaReceipt size={60} className="text-muted mb-3" />
                  <p className="text-muted">No voucher type data available</p>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default VouchersReport;